/**
 * ID3v2.3 writer — minimal implementation for preserving MP3 metadata through re-encode.
 *
 * Handles the frames mp3-compressor reads: TIT2 (title), TPE1 (artist), TALB (album),
 * TYER (year), TCON (genre), and APIC (cover art). Other frames are intentionally dropped —
 * they're uncommon on consumer MP3s and a full ID3v2 implementation is overkill for this use.
 *
 * Usage:
 *   const id3Bytes = ID3Writer.build({
 *       title: 'Song', artist: 'Artist', album: 'Album',
 *       year: '2024', genre: 'Rock',
 *       albumArt: { mime: 'image/jpeg', data: Uint8Array }
 *   });
 *   const taggedBlob = new Blob([id3Bytes, mp3Bytes], { type: 'audio/mpeg' });
 *
 * Why 2.3 and not 2.4: ID3v2.3 is the de-facto standard on Windows / iTunes / most media
 * players. v2.4 fixes some encoding edge cases but player support is uneven. For preservation
 * of simple text frames, v2.3 is the safer choice.
 */
(function(){
'use strict';

// Syncsafe 32-bit integer: top bit of each byte must be 0 (to avoid false MP3 sync words).
// Max representable: 2^28 - 1 = 256 MB which is plenty for any real tag.
function writeSyncsafe(n){
    return new Uint8Array([
        (n >>> 21) & 0x7F,
        (n >>> 14) & 0x7F,
        (n >>>  7) & 0x7F,
         n         & 0x7F
    ]);
}

// ID3v2.3 frames use a regular big-endian 32-bit size (not syncsafe).
function writeUint32BE(n){
    return new Uint8Array([(n>>>24)&0xFF, (n>>>16)&0xFF, (n>>>8)&0xFF, n&0xFF]);
}

// Text frames in v2.3 use either ISO-8859-1 (0x00) or UTF-16 with BOM (0x01).
// We always pick UTF-16 so non-Latin titles (Russian, Korean, Arabic, etc.) round-trip cleanly.
function encodeTextFrameUTF16(text){
    // BOM LE + UTF-16LE code units
    var bytes = [0xFF, 0xFE];
    for (var i = 0; i < text.length; i++) {
        var code = text.charCodeAt(i);
        bytes.push(code & 0xFF, (code >> 8) & 0xFF);
    }
    // Null terminator (UTF-16)
    bytes.push(0, 0);
    return new Uint8Array(bytes);
}

function buildTextFrame(frameId, text){
    if (!text || typeof text !== 'string') return null;
    var content = encodeTextFrameUTF16(text);
    // Payload: 1 byte encoding (0x01 = UTF-16 w/ BOM) + content
    var payload = new Uint8Array(1 + content.length);
    payload[0] = 0x01;
    payload.set(content, 1);
    // Frame: 4-byte ID + 4-byte size + 2-byte flags + payload
    var size = writeUint32BE(payload.length);
    var idBytes = new Uint8Array([frameId.charCodeAt(0), frameId.charCodeAt(1), frameId.charCodeAt(2), frameId.charCodeAt(3)]);
    var out = new Uint8Array(10 + payload.length);
    out.set(idBytes, 0);
    out.set(size, 4);
    // flags (2 bytes) left as 0
    out.set(payload, 10);
    return out;
}

// COMM frame (v2.3): 1 byte encoding + 3-byte language + short description (null-term)
// + comment text. Used by generated-audio tools for sample-license attribution
// (e.g. endless-piano's Salamander Grand Piano CC BY credit).
function buildCommFrame(text){
    if (!text || typeof text !== 'string') return null;
    var descBytes = encodeTextFrameUTF16('');       // empty description, UTF-16 null-term included
    var textBytes = encodeTextFrameUTF16(text);
    var payload = new Uint8Array(1 + 3 + descBytes.length + textBytes.length);
    var off = 0;
    payload[off++] = 0x01;                          // UTF-16 with BOM
    payload[off++] = 0x65; payload[off++] = 0x6E; payload[off++] = 0x67;   // 'eng'
    payload.set(descBytes, off); off += descBytes.length;
    payload.set(textBytes, off);
    var size = writeUint32BE(payload.length);
    var out = new Uint8Array(10 + payload.length);
    out.set([0x43, 0x4F, 0x4D, 0x4D], 0); // 'COMM'
    out.set(size, 4);
    out.set(payload, 10);
    return out;
}

// ID3v2 frame size is a 32-bit big-endian int, but the header containing all frames is
// 28-bit syncsafe (max 256 MB). Realistic ceiling for a single APIC is ~15 MB — larger
// images are almost always mis-exported and waste bandwidth. We skip oversized art
// rather than truncating.
var APIC_MAX_BYTES = 15 * 1024 * 1024;

function buildAPICFrame(mime, pictureType, description, imageData){
    // Payload: 1 encoding + mime (ASCII, null-term) + 1 picture type + description (ASCII, null-term) + image bytes
    if (!imageData || !imageData.length) return null;
    if (imageData.length > APIC_MAX_BYTES) {
        console.warn('[id3-writer] APIC (' + imageData.length + ' bytes) exceeds ' + APIC_MAX_BYTES + ' cap, dropping cover art');
        return null;
    }
    mime = mime || 'image/jpeg';
    pictureType = pictureType != null ? pictureType : 0x03; // 0x03 = Cover (front)
    description = description || '';

    var mimeBytes = new Uint8Array(mime.length);
    for (var i = 0; i < mime.length; i++) mimeBytes[i] = mime.charCodeAt(i) & 0xFF;

    var descBytes = new Uint8Array(description.length);
    for (var j = 0; j < description.length; j++) descBytes[j] = description.charCodeAt(j) & 0xFF;

    var payloadLen = 1 + mimeBytes.length + 1 + 1 + descBytes.length + 1 + imageData.length;
    var payload = new Uint8Array(payloadLen);
    var off = 0;
    payload[off++] = 0x00; // ASCII encoding for mime+description
    payload.set(mimeBytes, off); off += mimeBytes.length;
    payload[off++] = 0x00; // null after mime
    payload[off++] = pictureType;
    payload.set(descBytes, off); off += descBytes.length;
    payload[off++] = 0x00; // null after description
    payload.set(imageData, off);

    var size = writeUint32BE(payload.length);
    var out = new Uint8Array(10 + payload.length);
    out.set([0x41, 0x50, 0x49, 0x43], 0); // 'APIC'
    out.set(size, 4);
    out.set(payload, 10);
    return out;
}

function build(tags){
    if (!tags || typeof tags !== 'object') return new Uint8Array(0);

    var frames = [];
    var addFrame = function(f){ if (f) frames.push(f); };

    addFrame(buildTextFrame('TIT2', tags.title));
    addFrame(buildTextFrame('TPE1', tags.artist));
    addFrame(buildTextFrame('TALB', tags.album));
    addFrame(buildTextFrame('TYER', tags.year));
    addFrame(buildTextFrame('TCON', tags.genre));
    addFrame(buildCommFrame(tags.comment));

    if (tags.albumArt && tags.albumArt.data) {
        addFrame(buildAPICFrame(tags.albumArt.mime, 0x03, '', tags.albumArt.data));
    }

    if (frames.length === 0) return new Uint8Array(0);

    // Concatenate frames
    var totalFrameBytes = 0;
    for (var k = 0; k < frames.length; k++) totalFrameBytes += frames[k].length;
    var framesBytes = new Uint8Array(totalFrameBytes);
    var pos = 0;
    for (var m = 0; m < frames.length; m++) { framesBytes.set(frames[m], pos); pos += frames[m].length; }

    // ID3v2.3.0 header: "ID3" + version (0x03 0x00) + flags (0x00) + syncsafe size
    var header = new Uint8Array(10);
    header[0] = 0x49; header[1] = 0x44; header[2] = 0x33;
    header[3] = 0x03; // major version 3
    header[4] = 0x00; // revision
    header[5] = 0x00; // flags: no unsync, no extended, no experimental
    header.set(writeSyncsafe(totalFrameBytes), 6);

    var out = new Uint8Array(header.length + framesBytes.length);
    out.set(header, 0);
    out.set(framesBytes, header.length);
    return out;
}

window.ID3Writer = {
    build: build,
    version: '1.0.0'
};

})();
